<template>
    <div>
      <div class="text-center">
        <h4><a :href="adjusterLink"><i class="fa fa-user"></i> {{ adjusterName}}</a></h4>
      </div>

      <div v-if="error">
        <div class="alert alert-danger">
          {{ error }}
        </div>
      </div>

      <template v-if="nativeFields.length">
        <div class="row">
          <div v-for="field in nativeFields" class="col-md-12">

            <div class="form-group row my-3">
              <label :for="field.id" class="col-form-label col-sm-2">{{ field.name }}:</label>
              <div class="col-sm-10">
                <div v-html="nativeToHtml(nativeValues[field.id])" class="alert alert-secondary p-2 m-0"></div>
              </div>
            </div>

          </div>
        </div>
        <hr class="hr-line-dashed"/>
      </template>

      <template v-if="customFields.length">
        <div class="row">
          <div v-for="field in customFields" class="col-md-12">

            <div class="form-group row my-3">
              <label :for="field.id" class="col-form-label col-sm-2">{{ field.name }}:</label>
              <div class="col-sm-10">
                <component :is="customTypeConfig(field.type).widget_edit" v-model="fields.custom[field.id]" v-bind="field.params" :name="field.id"></component>
              </div>
            </div>

          </div>
        </div>
        <hr class="hr-line-dashed"/>
      </template>

      <div class="form-group row">
        <label for="notes" class="col-form-label col-sm-2">Notes:</label>
        <div class="col-sm-10">
          <textarea class="form-control" id="notes" v-model="notes" placeholder="Please enter a note, reason for editing, etc..."></textarea>
          <div v-if="errors && errors.notes" class="small text-danger">{{ errors.notes[0] }}</div>

          <template v-if="noteLog.length > 0">
            <div class="mt-2">
              <i class="fa fa-info-circle"></i> The most recent note:
              <div class="alert alert-info mb-0">
                {{ lastNote }}
              </div>
            </div>

            <a href="#" @click.prevent="toggleNoteLog" class="btn btn-sm btn-default my-2"><i class="fa fa-history"></i> Note log ({{ noteLog.length }}) <i class="fa" :class="{'fa-caret-up': noteLogOpened, 'fa-caret-down': !noteLogOpened}"></i></a>
            <div v-if="noteLogOpened">
              <table class="table table-striped mb-0">
                <tr v-for="note in noteLog">
                  <td class="w-150s">{{ note.date }}</td>
                  <td class="w-150s"><a :href="note.link">{{ note.user }}</a></td>
                  <td>{{ note.text }}</td>
                </tr>
              </table>
            </div>
          </template>
        </div>
      </div>

    </div>

    <hr class="hr-line-dashed"/>

    <div v-if="revision.author" class="text-muted text-right mb-3">
      Updated by <a :href="revision.link">{{ revision.author }}</a> at {{ revision.date }}
    </div>

    <div class="form-group mb-0">
      <div class="text-right">
        <a :href="returnLink" class="btn btn-default mr-1"><i class="fa fa-fw fa-ban"></i> Cancel</a>

        <button type="button" class="btn btn-primary" @click="submitData()" :disabled="processingSubmit">
          <i class="fa fa-fw fa-save"></i> Save
          <i class="fa fa-spinner fa-spin" v-if="processingSubmit"></i>
        </button>
      </div>
    </div>

</template>

<script>
import TextInput from "./widgets/edit/TextInput.vue";
import SelectList from "./widgets/edit/SelectList.vue";
import YesNoList from "./widgets/edit/YesNoList.vue";
import Date from "./widgets/edit/Date.vue";

export default {
  components: {
    text_input: TextInput,
    select_list: SelectList,
    yes_no_list: YesNoList,
    date: Date,
  },

  props: {
    action: {type: String, required: true},
    returnLink: {type: String, required: true},
    adjusterName: {type: String, required: true},
    adjusterLink: {type: String, required: true},

    nativeFieldConfigs: {type: Array, default: function () { return [] }},
    customFieldConfigs: {type: Array, default: function () { return [] }},

    id: {type: Number, default: 0},
    nativeValues: {type: Object, default: function () { return {} }},
    customValues: {type: Object, default: function () { return {} }},
    nativeFields: {type: Array, default: function () { return [] }},
    customFields: {type: Array, default: function () { return [] }},
    noteLog: {type: Array, default: function () { return [] }},
    revision: {type: Object, default: function () { return {author: '', link: '', date: ''} }},
  },

  /**
   * Variables (models).
   */
  data () {
    return {
      processingSubmit: false,
      error: '',
      errors: {},

      notes: '',
      noteLogOpened: false,

      // Form fields.
      fields: {
        native: this.nativeValues,
        custom: this.customValues,
      },
    }
  },

  /**
   * Computed values
   */
  computed: {

    lastNote() {
      return this.noteLog.length > 0 ? this.noteLog[this.noteLog.length - 1].text : '';
    },

  },

  /**
   * Functions (methods).
   */
  methods: {

    init() {
      this.fields.native = this.nativeValues;
      this.fields.custom = this.customValues;
      this.notes = '';
      this.noteLogOpened = false;
      this.clearValidation();
    },

    clearValidation() {
      this.errors = {};
      this.error = '';
    },

    customTypeConfig(type) {
      return this.customFieldConfigs.find(t => t.id === type);
    },

    toggleNoteLog() {
      this.noteLogOpened = !this.noteLogOpened;
    },

    nativeToHtml(value) {
      value = value.trim();
      return (value === '') ? '&nbsp;' : value;
    },

    submitData() {
      this.clearValidation();
      let formData = this.formData();
      this.processingSubmit = true;
      axios.post(this.action, formData, formData)
        .then(response => {
          window.location.href = response.data.redirect;
        })
        .catch(error =>{
          this.handleErrors(error, 'Unable to process request.');
          this.processingSubmit = false;
        });
    },

    formData() {
      const formData = new FormData();
      formData.append('_token', this.$csrf_token);
      formData.append('_method', "PATCH");
      formData.append('data', JSON.stringify(this.fields.custom));
      formData.append('notes', this.notes);
      return formData;
    },

    handleErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        let errors = errorObj.response.data.errors || {};
        let errorMessages = [];
        Object.keys(errors).forEach(field => {
          errorMessages.push(errors[field]);
        })
        this.error = errorMessages.join(' ');
      } else {
        this.error = defaultMessage;
      }
    },

  },
}
</script>
